import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Film, Loader2, Download, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useUserFiles } from '../contexts/UserFilesContext';
import { requestVideoRender, AudioFile } from '../services/api';
import { useVideoRenderPolling } from '../pages/Player/useVideoRenderPolling';
import { trackEvent } from '../services/analytics';
import type { BannerSpec } from './banners/types';

// localStorage key marking that the tutorial has been completed.
// "Completed" = the user clicked Download on a finished render at least
// once. Nothing else clears it — not dismiss, not start-of-render, not
// completion-without-download. Keyed per user sub so the flag travels
// with the user across logins and browser sessions on the same device.
const TUTORIAL_DONE_KEY_PREFIX = 'pw_video_tutorial_completed_';

const keyFor = (userId: string | null): string =>
  `${TUTORIAL_DONE_KEY_PREFIX}${userId ?? 'anonymous'}`;

export const readTutorialDone = (userId: string | null): boolean => {
  try {
    return localStorage.getItem(keyFor(userId)) === '1';
  } catch {
    return false;
  }
};

const markTutorialDone = (userId: string | null): void => {
  try {
    localStorage.setItem(keyFor(userId), '1');
  } catch {
    // private mode; non-fatal
  }
};

/**
 * Free-tier activation card for the share-video render feature. Shown
 * persistently (across navigation) to free users with artwork who haven't
 * yet downloaded a rendered video this session. The Download button click
 * is the only thing that retires the tutorial — dismiss (X) hides only the
 * current Home mount, and starting a render keeps the card visible so the
 * pending → ready → Download arc completes in-place.
 *
 * Showcase priority (highest first):
 *   - pending (videoSize === -1) — keep the user on the progress UI
 *   - ready (videoSize > 0 with videoUrl) — the user has a download waiting
 *   - idle (any other artworked file) — newest first
 *
 * If a render is already in progress on remount, polling auto-resumes — the
 * user sees the pending UI continue, not a fresh idle button that would
 * no-op against the server's conditional write.
 *
 * Educational-tier banner — the visible artwork is the affordance ("your
 * cover becomes a video"). Free renders carry a watermark; Pro removes it
 * (backend-enforced, copy reflects it).
 *
 * State machine mirrors Player/ActionsBar's Tier-1 video slot — idle / pending
 * / ready — so the user sees a continuous, in-place transition. No CLS:
 * the card's outer bounding box is fixed; only the button label/icon and
 * the small status sublabel change.
 *
 * Telemetry:
 *   - video_render_card_shown — fired once, on first paint of an eligible card
 *   - video_render_card_started — fired when the user taps the render button
 * Names must be added to lambda-events allowlist for server-side acceptance.
 */
const FreeUserVideoRenderCard: React.FC<{ onDismiss: () => void }> = ({ onDismiss }) => {
  const { getValidToken, getUserId } = useAuth();
  const userId = getUserId();
  const { showToast } = useToast();
  const { files } = useUserFiles();
  const { state, startPolling } = useVideoRenderPolling();
  const [requesting, setRequesting] = useState(false);
  const shownBeaconFiredRef = useRef(false);

  // Pick the showcase file by priority: pending render > ready download >
  // idle. Within each bucket, newest first — the file the user is most
  // likely to recognize and most likely to want to share. uploadedAt is a
  // number (Unix seconds) per the codebase's actual runtime shape; the
  // TypeScript declaration says string and lies. Same defensive idiom as
  // utils/fileUtils.ts:28.
  const showcaseFile: AudioFile | null = useMemo(() => {
    const artworked = files.filter((f) => !!f.artworkUrl);
    if (artworked.length === 0) return null;
    const ts = (f: AudioFile): number =>
      typeof f.uploadedAt === 'number' ? f.uploadedAt : new Date(f.uploadedAt).getTime() / 1000;
    const sorted = artworked.slice().sort((a, b) => ts(b) - ts(a));
    const pending = sorted.find((f) => f.videoSize === -1);
    if (pending) return pending;
    const ready = sorted.find(
      (f) => typeof f.videoSize === 'number' && f.videoSize > 0 && !!f.videoUrl,
    );
    if (ready) return ready;
    const idle = sorted.find(
      (f) => f.videoSize === undefined || f.videoSize === null || f.videoSize === 0 || f.videoSize === -2,
    );
    return idle ?? null;
  }, [files]);

  // Fire the shown beacon once per mount. Guarded by a ref so React 18 strict
  // double-mount in dev doesn't double-fire.
  useEffect(() => {
    if (shownBeaconFiredRef.current) return;
    if (!showcaseFile) return;
    shownBeaconFiredRef.current = true;
    trackEvent('video_render_card_shown', getValidToken);
  }, [showcaseFile, getValidToken]);

  // Auto-resume polling when the showcase file is mid-render (videoSize === -1).
  // Covers the navigate-away-and-back case: prior mount kicked off a render,
  // this mount sees the file still in flight via the file-list refresh, so we
  // pick up the polling instead of showing a fresh idle button.
  useEffect(() => {
    if (!showcaseFile) return;
    if (typeof showcaseFile.videoSize === 'number' && showcaseFile.videoSize === -1) {
      startPolling(showcaseFile.id);
    }
  }, [showcaseFile, startPolling]);

  if (!showcaseFile || readTutorialDone(userId)) return null;

  const handleRender = async () => {
    if (requesting) return;
    setRequesting(true);
    trackEvent('video_render_card_started', getValidToken);
    try {
      const token = await getValidToken();
      if (!token) {
        showToast('Sign in to export video', 'error');
        setRequesting(false);
        return;
      }
      await requestVideoRender(showcaseFile.id, token);
      startPolling(showcaseFile.id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not start render';
      showToast(msg, 'error');
      setRequesting(false);
    }
  };

  const status = state.status;
  const serverPending =
    typeof showcaseFile.videoSize === 'number' && showcaseFile.videoSize === -1;
  const isPending = requesting || status === 'pending' || serverPending;
  const fileVideoReady =
    typeof showcaseFile.videoSize === 'number' &&
    showcaseFile.videoSize > 0 &&
    !!showcaseFile.videoUrl;
  const isReady = fileVideoReady || (status === 'ready' && !!state.videoUrl);
  const videoUrlForDownload = state.videoUrl || showcaseFile.videoUrl || '';

  const handleDownloadClick = () => {
    markTutorialDone(userId);
    onDismiss();
  };
  const artworkSrc = showcaseFile.artworkThumbUrl || showcaseFile.artworkUrl || '';
  const trackTitle = showcaseFile.title || showcaseFile.filename || 'your track';

  return (
    <motion.div
      className="w-full rounded-2xl border p-6 md:p-8 relative overflow-hidden mb-8"
      style={{
        background: 'linear-gradient(135deg, rgba(255, 72, 0, 0.15) 0%, rgba(6, 182, 212, 0.15) 100%)',
        borderColor: 'rgba(255, 72, 0, 0.3)',
      }}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Decorative blurs matching the other top-slot cards so a returning user
          reads this as a variant of the same slot, not a new chrome. */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-soundwave-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-neon-500/10 rounded-full blur-3xl" />

      {/* Dismiss — small, top-right, low visual weight. The card is helpful,
          not a modal; respect the user who wants it gone. */}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="absolute top-3 right-3 z-20 p-1.5 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/5 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="relative z-10 flex flex-col md:flex-row items-center gap-5 md:gap-6">
        {/* Artwork — the affordance. Fixed 96px square so the bounding box
            never shifts as state changes. */}
        <div
          className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 shadow-lg ring-1 ring-white/10 bg-black/30"
          aria-hidden="true"
        >
          {artworkSrc ? (
            <img
              src={artworkSrc}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Film className="w-8 h-8 text-white/30" />
            </div>
          )}
        </div>

        {/* Copy column */}
        <div className="flex-1 text-center md:text-left">
          <h3 className="text-xl md:text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>
            Turn your cover into a video
          </h3>
          <p className="text-sm md:text-base" style={{ color: 'var(--text-secondary)' }}>
            We&rsquo;ll render <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{trackTitle}</span>{' '}
            as a video file — easy to post on Instagram, TikTok, or anywhere video plays. Free renders include a small watermark —{' '}
            <Link
              to="/profile/plan"
              className="underline decoration-soundwave-500/60 underline-offset-2 hover:decoration-soundwave-500 hover:text-soundwave-400 transition-colors"
              style={{ color: 'var(--text-primary)' }}
            >
              upgrade to Pro
            </Link>{' '}
            to remove it.
          </p>
        </div>

        {/* CTA column — fixed-width container so the button bounding box is
            stable through idle → pending → ready. */}
        <div className="flex flex-col items-stretch md:items-end gap-2 flex-shrink-0 w-full md:w-auto md:min-w-[200px]">
          {isReady ? (
            <a
              href={videoUrlForDownload}
              download
              onClick={handleDownloadClick}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-soundwave-500 to-soundwave-600 hover:from-soundwave-600 hover:to-soundwave-700 text-white font-bold rounded-xl shadow-lg transition-all"
            >
              <Download className="w-5 h-5" />
              Download video
            </a>
          ) : isPending ? (
            <button
              type="button"
              disabled
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-soundwave-500/60 text-white font-bold rounded-xl shadow-lg cursor-wait"
            >
              <Loader2 className="w-5 h-5 animate-spin" />
              Rendering&hellip;
            </button>
          ) : (
            <motion.button
              type="button"
              onClick={handleRender}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-soundwave-500 to-soundwave-600 hover:from-soundwave-600 hover:to-soundwave-700 text-white font-bold rounded-xl shadow-lg transition-all"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <Film className="w-5 h-5" />
              Render video
            </motion.button>
          )}
          <p className="text-[11px] text-center md:text-right" style={{ color: 'var(--text-tertiary)' }}>
            {isReady ? 'Saved to your files.' : 'Usually under a minute.'}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default FreeUserVideoRenderCard;

/**
 * Educational-tier spec: shown to free users with at least one artworked
 * file who haven't yet downloaded a rendered video. Suppressed by the
 * per-mount `videoCardDismissed` flag (X-click on the current Home view)
 * and by the persistent `pw_video_tutorial_completed_<sub>` flag (Download
 * click on a finished render).
 *
 * Beats the expiring-files nudge deliberately — a missed urgency for a day
 * is worth less than a missed activation opportunity.
 */
export const freeUserVideoRenderCardSpec: BannerSpec = {
  id: 'video-render-card',
  category: 'educational',
  when: ({ userId, userFiles, videoCardDismissed }) =>
    !videoCardDismissed &&
    !!userId &&
    userFiles.some((f) => !!f.artworkUrl) &&
    !userFiles.some(
      (f) => typeof f.videoSize === 'number' && f.videoSize > 0 && !!f.videoUrl,
    ) &&
    !readTutorialDone(userId),
  render: ({ onDismissVideoCard }) => (
    <FreeUserVideoRenderCard onDismiss={onDismissVideoCard} />
  ),
};
